import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { addWeightLog, getWeightLogs } from '../../services/firestoreService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';
import { format, differenceInDays } from 'date-fns';
import toast from 'react-hot-toast';

export default function WeightTracker() {
  const { theme } = useTheme();
  const { user, userProfile } = useAuth();
  const [logs, setLogs] = useState([]);
  const [weight, setWeight] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const goalWeight = userProfile?.goals?.targetWeight;
  const startWeight = userProfile?.currentWeight;

  useEffect(() => {
    if (user) loadLogs();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLogs = async () => {
    const data = await getWeightLogs(user.uid, 90);
    setLogs(data.reverse());
  };

  const handleLog = async (e) => {
    e.preventDefault();
    if (!weight) return;
    setLoading(true);
    try {
      await addWeightLog(user.uid, {
        weight: parseFloat(weight),
        note,
        date: format(new Date(), 'yyyy-MM-dd'),
      });
      toast.success(`✅ ${weight} lbs logged!`);
      setWeight('');
      setNote('');
      loadLogs();
    } catch {
      toast.error('Failed to log weight');
    } finally {
      setLoading(false);
    }
  };

  const currentWeight = logs[logs.length - 1]?.weight || startWeight;
  const totalLost = startWeight && currentWeight ? (startWeight - currentWeight).toFixed(1) : null;
  const toGoal = goalWeight && currentWeight ? Math.abs(currentWeight - goalWeight).toFixed(1) : null;

  // Weekly projection
  const weeklyRate = () => {
    if (logs.length < 2) return null;
    const first = logs[0];
    const last = logs[logs.length - 1];
    const days = differenceInDays(
      new Date(last.date || Date.now()),
      new Date(first.date || Date.now())
    );
    if (days < 7) return null;
    const lbsPerDay = (first.weight - last.weight) / days;
    return (lbsPerDay * 7).toFixed(1);
  };

  const weeksToGoal = () => {
    const rate = weeklyRate();
    if (!rate || !toGoal || parseFloat(rate) <= 0) return null;
    return Math.ceil(parseFloat(toGoal) / parseFloat(rate));
  };

  const chartData = logs.map(l => ({
    date: l.date || 'N/A',
    weight: l.weight,
  }));

  const progressPct = startWeight && goalWeight && currentWeight
    ? Math.min(100, Math.max(0, Math.round(((startWeight - currentWeight) / (startWeight - goalWeight)) * 100)))
    : 0;

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px' }} className="animate-fade">
      <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: theme.text, marginBottom: '2rem' }}>
        ⚖️ Weight Tracker
      </h1>

      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { label: 'Current', value: currentWeight ? `${currentWeight} lbs` : '--', color: theme.accent },
          { label: 'Goal', value: goalWeight ? `${goalWeight} lbs` : '--', color: theme.success },
          { label: totalLost >= 0 ? 'Lost' : 'Gained', value: totalLost ? `${Math.abs(totalLost)} lbs` : '--', color: parseFloat(totalLost) > 0 ? theme.success : theme.warning },
          { label: 'To Goal', value: toGoal ? `${toGoal} lbs` : '--', color: theme.warning },
          { label: 'Avg/Week', value: weeklyRate() ? `${weeklyRate()} lbs` : '--', color: theme.textSecondary },
          { label: 'Est. Weeks', value: weeksToGoal() ? `${weeksToGoal()} wks` : '--', color: theme.textSecondary },
        ].map((stat, i) => (
          <div key={i} className="card" style={{ textAlign: 'center' }}>
            <div style={{ color: theme.textSecondary, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>{stat.label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Progress Bar */}
      {progressPct > 0 && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, color: theme.text }}>Goal Progress</span>
            <span style={{ color: theme.accent, fontWeight: 700 }}>{progressPct}%</span>
          </div>
          <div style={{ height: '16px', background: theme.border, borderRadius: '8px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${progressPct}%`,
              background: `linear-gradient(90deg, ${theme.accent}, ${theme.success})`,
              borderRadius: '8px', transition: 'width 1.2s ease',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                color: '#fff', fontSize: '0.65rem', fontWeight: 700,
              }}>{progressPct >= 15 ? `${progressPct}%` : ''}</div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.4rem', fontSize: '0.75rem', color: theme.textSecondary }}>
            <span>Start: {startWeight} lbs</span>
            <span>Goal: {goalWeight} lbs</span>
          </div>
        </div>
      )}

      {/* Log Weight Form */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, marginBottom: '1rem', color: theme.text }}>Log Today's Weight</h3>
        <form onSubmit={handleLog} style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ flex: '0 0 140px', marginBottom: 0 }}>
            <label>Weight (lbs)</label>
            <input type="number" step="0.1" value={weight} onChange={e => setWeight(e.target.value)} placeholder="185.5" required />
          </div>
          <div className="form-group" style={{ flex: 1, minWidth: '200px', marginBottom: 0 }}>
            <label>Note (optional)</label>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. After morning workout..." />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : '+ Log Weight'}
          </button>
        </form>
      </div>

      {/* Chart */}
      {chartData.length > 1 && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, marginBottom: '1rem', color: theme.text }}>Weight History</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
              <XAxis dataKey="date" tick={{ fill: theme.textSecondary, fontSize: 11 }} />
              <YAxis domain={['auto', 'auto']} tick={{ fill: theme.textSecondary, fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: '8px', color: theme.text }}
                formatter={(v) => [`${v} lbs`, 'Weight']}
              />
              {goalWeight && <ReferenceLine y={goalWeight} stroke={theme.success} strokeDasharray="5 5" label={{ value: 'Goal', fill: theme.success, fontSize: 11 }} />}
              <Line type="monotone" dataKey="weight" stroke={theme.accent} strokeWidth={2.5} dot={{ fill: theme.accent, r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Log Table */}
      {logs.length > 0 && (
        <div className="card">
          <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, marginBottom: '1rem', color: theme.text }}>Log History</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                  {['Date', 'Weight', 'Change', 'Note'].map(h => (
                    <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', color: theme.textSecondary, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...logs].reverse().map((log, i, arr) => {
                  const prev = arr[i + 1];
                  const change = prev ? (log.weight - prev.weight).toFixed(1) : null;
                  return (
                    <tr key={log.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.text }}>{log.date}</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.accent, fontWeight: 600 }}>{log.weight} lbs</td>
                      <td style={{ padding: '0.6rem 0.75rem', color: change ? (parseFloat(change) < 0 ? theme.success : theme.danger) : theme.textSecondary }}>
                        {change ? `${parseFloat(change) > 0 ? '+' : ''}${change}` : '--'}
                      </td>
                      <td style={{ padding: '0.6rem 0.75rem', color: theme.textSecondary, fontSize: '0.8rem' }}>{log.note || '--'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
